import { useState, type ReactNode } from 'react'

import type { BatchListRow } from '@ctcells/contract'
import { t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { Pagination } from '../../atoms/Pagination.tsx'
import type { StateNoticeTone } from '../../atoms/StateNotice.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import { Toolbar } from '../../atoms/Toolbar.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { useGroupTabLayout } from '../groupTabs.ts'
import { OPS_TABS, type OpsTabId } from '../ops/opsTabs.ts'
import { gate, type ScreenAccess } from '../access.ts'
import { DEMO_BATCH_LIST } from '../demoData.ts'
import { withPriority } from '../listColumnPriority.ts'
import type { ListPaging } from '../listPaging.ts'

import { batchIdColumn } from './batchIdColumn.tsx'
import { batchOwnerColumn, ownerSearchTexts, type BatchOwners } from './batchOwnerColumn.tsx'
import { batchSampleCountColumn } from './batchSampleCountColumn.tsx'
import { batchStatusColumn } from './batchStatusColumn.tsx'
import { batchCreatedColumn, batchEndedColumn, batchStartedColumn } from './batchTimeColumns.tsx'

import '../screens.css'

/**
 * 운영 › 작업 목록 — 와이어프레임 `batch()` 2188행.
 *
 * 🔴 **아직 끝나지 않은 작업만** 보인다. 종착(`completed` · `aborted`)은 운영 › 이력으로 넘어간다
 *    (2026-09-15 지시 · 2192행 주석).
 * 🔴 실행 전 상태는 `created` 하나다. `queued` 를 쓰지 마라 — 트리거 API 가 없다(§5-3).
 */

/**
 * 처음 정렬 — **서버 차례와 같다**(`GET /batches?phase=active` = `created_at desc` · `app/services/batches.py` `_order`).
 * 🔴 **머리글 정렬은 받은 쪽 안에서만이다**(eric 2026-09-30 「모든 게시판」). `GET /batches` 에 정렬 인자가 없어
 *    (openapi 실측 `limit · offset · status · recipe_id · review · phase`) 「생성 오름차순」은 «이 쪽에서 가장 오래된
 *    것»이지 전체에서가 아니다. ⚠ 검색칸 옆 안내(「검색 · 정렬은 지금 쪽 안에서만」)는 eric 지시로 뺐다(2026-09-30
 *    「이거 빼줘」) — 그 사실은 이 주석에만 남는다. 되살리지 마라.
 *    서버 정렬 인자 요청은 `docs/backend-requests.csv` BR-111.
 */
const BATCHES_DEFAULT_SORT: TableSort = { key: 'created', direction: 'desc' }

export interface BatchesScreenProps {
  /**
   * 열린 운영 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다(`ops/opsTabs.ts`).
   * ⚠ **넘기지 않으면 탭 줄을 그리지 않는다.** 태블릿 kiosk 가 이 화면을 그대로 싣는데
   *   (`routes/tabletRoutes.tsx`) 거기서 콘솔 경로로 이동하면 kiosk 밖으로 튕겨 나간다.
   *   준비·관리 무리와 달리 **운영 3종만 태블릿과 겹쳐서** 이 갈래가 필요하다.
   */
  opsTab?: OpsTabId | undefined

  rows?: readonly BatchListRow[]
  access?: ScreenAccess
  loading?: boolean
  /**
   * 목록을 **못 본 까닭**. 🔴 이게 없어서 조회 실패가 「작업 없음」으로 나갔다(2026-09-22).
   *    `rows` 가 비었다는 사실과 **목록을 받지 못했다**는 사실은 다르다.
   */
  notice?: string | undefined
  /** 사유의 성격. 통신 실패·5xx 는 `failed`, 권한·잠김은 기본값 */
  noticeTone?: StateNoticeTone | undefined
  onOpenBatch?: (batchId: string) => void
  onNewBatch?: () => void
  /**
   * 주 버튼 왼쪽에 서는 **개발용** 요소(「테스트 작업 시작」).
   *
   * 🔴 **컨테이너가 넘길 때만 있다.** 화면이 개발 플래그를 읽지 않는다 — 이 화면은 태블릿도
   *    그대로 싣는데(`routes/tabletViews.tsx`), 넘기지 않는 표면은 예전과 한 픽셀도 같다.
   * ⚠ 자리를 왼쪽으로 둔 것은 의도다. 주 버튼(＋ 새 작업)이 오른쪽 끝이라는 규칙(§1-3)을
   *   개발용 버튼이 밀어내면 안 된다.
   */
  devAction?: ReactNode
  /** 오른쪽 판에 열린 작업 — 그 행을 고른 것으로 표시한다(eric 2026-09-22) */
  selectedBatchId?: string | undefined
  /**
   * 작업자 이름표. 🔴 **넘겼을 때만 「작업자」 열이 선다**(admin 전용 · `batchOwnerColumn.tsx` 머리말).
   * 넘길지는 컨테이너가 정한다 — 화면은 역할을 모른다.
   */
  owners?: BatchOwners | undefined
  /**
   * 쪽 나눔(eric 2026-09-30 · `screens/listPaging.ts`). 🔴 **넘겼을 때만 쪽 넘김이 선다.** 쪽을 받는 일은
   * 컨테이너가 한다 — 화면은 번호만 올린다. 검색이 바뀌면 1쪽으로 돌린다.
   */
  paging?: ListPaging | undefined
}

export function BatchesScreen({
  opsTab,
  rows = DEMO_BATCH_LIST,
  access = {},
  loading = false,
  notice,
  noticeTone,
  onOpenBatch,
  onNewBatch,
  devAction,
  selectedBatchId,
  owners,
  paging,
}: BatchesScreenProps) {
  // 🔴 「작업」 무리의 탭 줄. 그릴지 말지는 `useGroupTabLayout` 한 곳이 정한다(규칙은 거기 주석)
  const groupTabs = useGroupTabLayout(OPS_TABS, opsTab, 'batches', access)
  // 이 탭에 필요한 권한이 없으면 내용 대신 사유만 낸다(아래 본문 갈래). 사유 문자열 또는 null
  const viewBlocked = gate(access, { perm: 'view' }).disabledReason ?? null

  const [query, setQueryState] = useState('')
  // 🔴 검색이 바뀌면 1쪽이다 — 3쪽을 보다 검색하면 그 결과의 3쪽이 아니라 처음부터 본다
  const setQuery = (next: string): void => {
    setQueryState(next)
    paging?.onChange(1)
  }

  /*
   * 🔴 **상태 거름 알약 · 정렬 버튼은 없다**(eric 2026-09-30 「전체 · 대기 · 실행 중 · 복구 대기 이거 다
   *    빼고.. 정렬 최근 만든 순 버튼도 없애줘」). 목록은 늘 전체이고, 차례는 **서버 차례 그대로**다
   *    (서버가 `created_at desc` 로 세운다). 처음 차례는 그 차례 그대로이고, 머리글을 누르면 **받은 쪽 안에서만**
   *    다시 선다(eric 2026-09-30 「모든 게시판」 · `BATCHES_DEFAULT_SORT`).
   * 🔴 **검색은 «받은 쪽» 안에서만 걸린다**(2026-09-30 실측). `GET /batches` 에 검색 인자가 없다.
   *    검색칸 옆 안내는 eric 지시로 뺐다(2026-09-30) — 이 사실은 주석에만 남는다.
   */
  const needle = query.trim().toLowerCase()
  const narrowed = needle !== ''
  const multiPage = paging !== undefined && paging.pageCount > 1
  const shown =
    needle === ''
      ? rows
      : rows.filter((row) =>
          [row.batch_id, row.recipe_name, ...ownerSearchTexts(owners, row.created_by)].some((v) =>
            v.toLowerCase().includes(needle),
          ),
        )

  /*
   * 🔴 **「시료 수」는 2026-09-26 에 되살렸다** — `Batch.sample_count` 가 응답에 생겼다(실측 50/50).
   * 🔴 **「작업자」(종전 「담당」)는 admin 에게만 이름으로 선다**(eric 2026-09-30 「그거 admin 만 작업자 보이게 해줘」 ·
   *    `owners` 를 받았을 때만). 검색도 그때만 이름 · 계정명으로 걸린다 — 계정 ID 로는 걸지 않는다
   *    (화면에 없는 값으로 걸리면 왜 걸렸는지 모른다). 까닭은 `batchOwnerColumn.tsx` 머리말.
   */
  /*
   * 🔴 **첫 열은 작업 ID 다**(eric 2026-09-22 「작업 데이터인데 작업 ID 가 가장 먼저 와야지.
   *    그 다음이 레시피고」). 같은 날 오전에 「첫 열의 주인공은 레시피 이름」으로 갔던 것을
   *    **eric 이 뒤집었다** — 옛 문장을 남기지 않는다(남기면 다음 사람이 되돌린다).
   * 🔴 **행을 여는 링크도 첫 칸으로 따라온다.** 첫 칸이 진짜 `button` 이어야 키보드로 갈 수 있다
   *    (§11-4-7 — 와이어프레임의 `tr.rowlink` 를 쓰지 않는 까닭).
   * 🔴 **넉넉하면 ID 36자 그대로, 서랍이 열려 모자랄 때만 앞 8자**다(eric 2026-09-28 「줄여」 — 2026-09-22
   *    「짤리는데? 숨김 처리하면 안 되지」를 그 경우에 한해 뒤집었다 · `batchIdColumn`).
   * 🔴 **열 차례는 이력과 같다 — 소요 시간만 없다**(eric 2026-09-30 「진행 중 탭 컬럼도.. 이력 컬럼에서 소요 시간
   *    빼고 동일하게 해줘」): 작업 ID → 상태 → 시료 수 → 레시피 → (작업자 · admin 만) → 생성 → 시작 → 종료.
   *    열 정의도 이력과 **같은 함수**다(`batchIdColumn` · `batchStatusColumn` · `batchSampleCountColumn` ·
   *    `batchOwnerColumn` · `batchTimeColumns`). 「그 다음이 레시피」(2026-09-22)는 이 차례로 바뀌었다.
   * 🔴 **「진행」 열은 없다**(같은 지시 — 이력 열에 없다). 진행은 구독 값이라 상시 작업 줄 · 모니터링이 말한다.
   *    그래서 목록 행(`BatchListRow`)에서도 진행 칸을 걷었다 — 되살리면 구독 보관소를 컨테이너에 다시 잇는 일이다.
   * 🔴 **「종료」 열이 있다** — 진행 중 목록이라 대개 `—` 이고, 종착 직후(목록이 다시 받기 전) 잠깐 값이 선다.
   *    레시피 이름 옆 버전(`v1`)은 진행 중 목록에만 있다(종전 그대로).
   */
  const columns: Column<BatchListRow>[] = [
    // 🔴 첫 열 · 폭이 모자라면 앞 8자로 준다(eric 2026-09-28 「줄여」) — `batchIdColumn` 머리말
    batchIdColumn(onOpenBatch),
    /*
     * 🔴 **서버가 「왜 멈췄나」를 안다** — 진행 중 목록은 `error` 행에 「복구 대기」와 `halt_reason` 한 줄이
     *    알약 옆에 붙는다(`withHalt` · 백엔드 `AH-17`). 이력과 같은 열 정의다
     */
    batchStatusColumn({ withHalt: true }),
    batchSampleCountColumn(),
    {
      key: 'recipe',
      header: t('column.recipe'),
      /*
       * 🔴 **한 줄로 세운다**(eric 5178 실측 2026-09-22). 서랍이 열린 폭(`.splitpage--open` 의
       *    5/11)에서 `fe-20260922-full-r1` 같은 이름이 **세 줄로 꺾여** 행이 높아졌다.
       *    넘치면 표가 가로로 스크롤하는 것이 맞다 — 잘라 숨기는 것만 금지다(작업 ID 와 같은 규칙).
       */
      nowrap: true,
      sort: 'text',
      sortValue: (row) => row.recipe_name,
      /*
       * ⚠ **`btnrow`(flex-wrap) 로 감싸지 않는다**(2026-09-26 5180 실측) — 열이 늘어난 뒤 서랍 폭에서
       *   `v1` 이 **아랫줄로 꺾였다.** 인라인 두 조각 + 사이 공백이면 칸의 `nowrap` 이 그대로 먹는다.
       */
      cell: (row) => (
        <span>
          <span>{row.recipe_name}</span>{' '}
          {/*
            🔴 **실행한 committed 레시피 버전**(`Batch.recipe_version` — 응답에 있는 값).
               레시피는 재커밋으로 버전이 오르므로 이름만으로는 어느 벌로 돌았는지 모른다.
            🔴 **별도 열을 만들지 않는다** — 작업 줄·작업 상세가 이미 이름 옆에 `v1` 로 붙인다.
          */}
          {row.recipe_version === null ? null : (
            <span className="muted">{`v${String(row.recipe_version)}`}</span>
          )}
        </span>
      ),
    },
    // 🔴 admin 에게만(`owners` 를 받았을 때만) 선다 — 자리는 레시피 뒤
    ...(owners === undefined ? [] : [batchOwnerColumn(owners)]),
    // 🔴 이력과 **같은 열 정의**다(`batchTimeColumns.tsx`)
    batchCreatedColumn(),
    batchStartedColumn(),
    batchEndedColumn(),
  ]

  return (
    <ListPage
      /* 탭 줄은 「작업」 무리 탭뿐이다. 무리 탭이 없는 표면(태블릿)은 탭 줄 자체가 없다 */
      {...(groupTabs
        ? { onTabChange: groupTabs.onChange, tabValue: groupTabs.value, tabs: groupTabs.tabs }
        : {})}
      primaryAction={
        <>
          {devAction}
          <Button
            onClick={onNewBatch}
            size="pill"
            variant="primary"
            {...gate(access, { perm: 'control' })}
          >
            {t('action.newBatch')}
          </Button>
        </>
      }
      tabsLabel={t('screen.batches.title')}
      /* 무리 탭이 있으면 제목도 무리 이름이다(준비·관리와 같은 규칙). 태블릿은 화면 제목 그대로 */
      title={groupTabs ? t('screen.opsGroup.title') : t('screen.batches.title')}
      toolbar={
        <Toolbar label={t('screen.batches.title')}>
          <SearchInput
            label={t(
              owners === undefined ? 'filter.search.batches' : 'filter.search.batchesWithOwner',
            )}
            onChange={setQuery}
            placeholder={t('filter.search.batchesPlaceholder')}
            value={query}
          />
        </Toolbar>
      }
    >
      {/*
        🔴 **무리 안에서는 이 탭의 권한을 내용에도 건다**(2026-09-18). 이력·알람이 「작업」으로
           들어오면서 사이드바 문이 `monitor` 로 내려갔다 — 그래서 `view` 없는 역할(`service`)도
           이 주소로 **들어올 수는 있다.** 탭만 잠그고 내용을 내주면 막은 것이 아니다.
           탭 줄은 남긴다 — 알람 탭으로 갈 길이 필요하다.
        ⚠ 태블릿(무리 탭 없음)에서는 이 갈래가 돌지 않는다. 그쪽은 예전과 같다.
        ⚠ **집행은 서버 403 `INSUFFICIENT_ROLE`** 이다. 여기서 막았다고 안전해지는 것이 아니다.
      */}
      {groupTabs && viewBlocked ? (
        <p className="deny" role="status">
          {viewBlocked}
        </p>
      ) : (
        <>
          <DataTable
            selected={(row) => row.batch_id === selectedBatchId}
            caption={t('screen.batches.title')}
            /* 🔴 공간이 모자라면 덜 중요한 열부터 빠진다 — 차례는 `listColumnPriority.ts` 한 곳 */
            columns={withPriority('batches', columns)}
            // 🔴 남는 폭은 모든 열이 똑같이 나눈다 — 이력과 같은 규칙(eric 2026-09-30 · `DataTable.columnWidths`)
            columnWidths="even"
            defaultSort={BATCHES_DEFAULT_SORT}
            emphasis={(row) => row.status === 'error'}
            emptyLabel={t('state.empty.batches')}
            loading={loading}
            {...(notice === undefined ? {} : { notice })}
            {...(noticeTone === undefined ? {} : { noticeTone })}
            /*
             * 🔴 **행 아무 칸이나 눌러도 열린다**(eric 2026-09-23). 첫 칸의 작업 ID 버튼은
             *    그대로 남는다 — 키보드로 가는 길은 그것뿐이다(`DataTable.onRowClick` 주석).
             */
            onRowClick={onOpenBatch === undefined ? undefined : (row) => onOpenBatch(row.batch_id)}
            rowKey={(row) => row.batch_id}
            rows={shown}
            /*
             * 🔴 「N건」은 **전체 건수**(`total_count`)다. 검색으로 이 쪽 안을 걸렀으면 걸린 행 수다
             *    (검색 · 정렬은 받은 쪽 안에서만 걸린다 — 안내 글자는 eric 지시로 뺐다)
             */
            {...(narrowed || paging?.totalCount == null ? {} : { rowCount: paging.totalCount })}
          />
          {/* 🔴 한 쪽뿐이면 쪽 조작이 없다 */}
          {multiPage ? (
            <Pagination
              label={t('screen.batches.title')}
              onChange={paging.onChange}
              page={paging.page}
              pageCount={paging.pageCount}
            />
          ) : null}
        </>
      )}
    </ListPage>
  )
}
